package com.tixforgigs.common.core.util

import com.tixforgigs.apax.config.models.Flavor
import com.tixforgigs.apax.models.ApiError
import com.tixforgigs.apax.models.OAuthError
import com.tixforgigs.common.core.extensions.orElse
import com.tixforgigs.common.core.imports.toastMessage.ToastContainer
import com.tixforgigs.common.core.imports.toastMessage.toast
import com.tixforgigs.common.http.HttpClient.FLAVOR
import io.ktor.client.statement.*
import js.objects.unsafeJso
import react.create
import react.dom.client.Root
import react.dom.client.createRoot
import web.dom.ElementId
import web.dom.document
import kotlin.js.Promise

actual object ToastUtil {

  actual val TOAST_MESSAGES_CONTAINER_ID: String
    get() = "toast-messages-container"
  actual var toastRoot: Root? = null

  actual enum class ToastType { INFO, SUCCESS, WARNING, ERROR }

  actual fun init() {
    if (toastRoot != null) return

    val container = (document.getElementById(ElementId(TOAST_MESSAGES_CONTAINER_ID)) ?: run {
      val div = document.createElement("div")
      div.id = ElementId(TOAST_MESSAGES_CONTAINER_ID)
      document.body.appendChild(div)
      div
    })

    toastRoot = createRoot(container)
    toastRoot?.render(
      ToastContainer.create {
        position = "bottom-left"
        theme = "dark"
      }
    )
  }

  actual fun showToast(message: String, type: ToastType, options: ToastOptions?) {
    when (type) {
      ToastType.INFO -> toast.info(message, options?.toToastOptionsJs().orElse { unsafeJso() })
      ToastType.SUCCESS -> toast.success(message, options?.toToastOptionsJs().orElse { unsafeJso() })
      ToastType.WARNING -> toast.warn(message, options?.toToastOptionsJs().orElse { unsafeJso() })
      ToastType.ERROR -> toast.error(message, options?.toToastOptionsJs().orElse { unsafeJso() })
    }
  }

  actual fun showInfoToast(message: String, options: ToastOptions?) {
    showToast(message, ToastType.INFO, options)
  }

  actual fun showSuccessToast(message: String, options: ToastOptions?) {
    showToast(message, ToastType.SUCCESS, options)
  }

  actual fun showWarningToast(message: String, options: ToastOptions?) {
    showToast(message, ToastType.WARNING, options)
  }

  actual fun showErrorToast(message: String, options: ToastOptions?) {
    showToast(message, ToastType.ERROR, options)
  }

  actual fun showThrowableToast(
    throwable: Throwable?,
    apiError: ApiError?,
    oauthError: OAuthError?,
    httpResponse: HttpResponse?,
    options: ToastOptions?
  ) {
    val message = buildString {
      httpResponse?.let {
        append("HTTP ${it.status.value} ${it.status.description}: ")
      }
      throwable?.let {
        appendLine("THROWABLE ")
        appendLine("MESSAGE: ${it.message} | ")
      }
      apiError?.let {
        appendLine("API ERROR ")
        appendLine("TYPE: ${it.type} | ")
        appendLine("TITLE: ${it.title} | ")
        appendLine("DETAILS: ${it.detail} | ")
        appendLine("PROPERTIES: ${it.properties} ")
      }
      oauthError?.let {
        appendLine("OAUTH ERROR ")
        appendLine("ERROR: ${it.error.value}")
        it.errorDescription?.let { description ->
          appendLine("DESCRIPTION: $description")
        }
        it.errorUri?.let { uri ->
          appendLine("URI: $uri")
        }
      }
    }
    when (FLAVOR) {
      Flavor.LOCAL, Flavor.DEV -> showErrorToast(message, options)
      Flavor.STAGING, Flavor.BETA, Flavor.PROD -> console.warn(message)
    }
  }

  actual fun <T> showPromiseToast(promise: Promise<T>, messages: ToastPromiseMessages, options: ToastOptions?) {
    toast.promise(promise, messages.toToastPromiseMessagesJs(), options?.toToastOptionsJs().orElse { unsafeJso() })
  }

  actual interface ToastOptions {

    actual var position: String?
    actual var autoClose: Any
    actual var hideProgressBar: Boolean?
    actual var closeOnClick: Boolean?
    actual var pauseOnHover: Boolean?
    actual var draggable: Boolean?
    actual var progress: Number?
    actual var theme: String?
  }

  actual interface ToastPromiseMessages {
    actual var pending: String?
    actual var success: String?
    actual var error: String?
  }

  fun ToastOptions.toToastOptionsJs(): com.tixforgigs.common.core.imports.toastMessage.ToastOptions =
    unsafeJso {
      position = this@toToastOptionsJs.position
      autoClose = this@toToastOptionsJs.autoClose.asDynamic()
      hideProgressBar = this@toToastOptionsJs.hideProgressBar
      closeOnClick = this@toToastOptionsJs.closeOnClick
      pauseOnHover = this@toToastOptionsJs.pauseOnHover
      draggable = this@toToastOptionsJs.draggable
      progress = this@toToastOptionsJs.progress
      theme = this@toToastOptionsJs.theme
    }

  fun ToastPromiseMessages.toToastPromiseMessagesJs(): com.tixforgigs.common.core.imports.toastMessage.ToastPromiseMessages =
    unsafeJso {
      pending = this@toToastPromiseMessagesJs.pending
      success = this@toToastPromiseMessagesJs.success
      error = this@toToastPromiseMessagesJs.error
    }
}